import React, { forwardRef } from "react";
import type { CampaignReportResponse } from "../../types";
import { ClientLogo } from "../../components/ClientLogo";
import { KpiGridModule } from "../modules/KpiGridModule";
import { KpiHeroModule } from "../modules/KpiHeroModule";
import { TrendLineModule } from "../modules/TrendLineModule";
import { BrandShareModule } from "../modules/BrandShareModule";
import { BrandComparisonModule } from "../modules/BrandComparisonModule";
import { CampaignTableModule } from "../modules/CampaignTableModule";
import { KeyCalloutsModule } from "../modules/KeyCalloutsModule";
import type {
  ExportTemplateDefinition,
  ExportTemplateModule,
} from "../types";

export interface WidescreenExportCanvasProps {
  data: CampaignReportResponse;
  template: ExportTemplateDefinition;
  callouts?: string;
  metadata?: {
    retailer?: string;
    client?: string;
    channel?: string;
  };
}

function renderTemplateModule(
  module: ExportTemplateModule,
  props: WidescreenExportCanvasProps,
): React.ReactNode {
  switch (module.type) {
    case "kpi-grid":
      return <KpiGridModule summary={props.data.summary} module={module} />;
    case "kpi-hero":
      return <KpiHeroModule summary={props.data.summary} module={module} />;
    case "trend-line":
      return <TrendLineModule data={props.data} module={module} />;
    case "brand-share":
      return <BrandShareModule data={props.data} module={module} />;
    case "brand-comparison":
      return <BrandComparisonModule data={props.data} module={module} />;
    case "campaign-table":
      return <CampaignTableModule data={props.data} module={module} />;
    case "callouts":
      return (
        <KeyCalloutsModule
          data={props.data}
          module={module}
          callouts={props.callouts}
        />
      );
  }
}

export const CANVAS_WIDTH = 1920;
export const CANVAS_HEIGHT = 1080;

export const WidescreenExportCanvas = forwardRef<
  HTMLDivElement,
  WidescreenExportCanvasProps
>(({ data, template, callouts, metadata }, ref) => {
  const heroClient = metadata?.client || "Client";
  const heroRetailer = metadata?.retailer || data.report_type;
  const heroChannel = metadata?.channel || "All Channels";
  const heroDateRange = `${data.summary.date_range_start} → ${data.summary.date_range_end}`;

  return (
    <div
      ref={ref}
      className="rounded-[40px] overflow-hidden shadow-[0_45px_90px_rgba(15,23,42,0.15)] border border-white/40 font-sans"
      style={{
        width: `${CANVAS_WIDTH}px`,
        height: `${CANVAS_HEIGHT}px`,
        background: `linear-gradient(135deg, ${
          template.background?.gradientFrom ?? "#0f172a"
        }, ${template.background?.gradientTo ?? "#312e81"})`,
      }}
      data-export-target
    >
      <div className="h-full w-full flex flex-col text-slate-900">
        {/* Header */}
        <div className="px-14 pt-12 pb-8">
          <div className="rounded-[28px] bg-white/95 backdrop-blur-sm border border-white/80 shadow-[0_25px_70px_rgba(15,23,42,0.12)] px-12 py-9 flex items-start justify-between gap-10">
            <div className="space-y-5">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-50 text-rose-600 text-xs font-semibold tracking-[0.35em] uppercase">
                Campaign Snapshot
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-slate-400 mb-3">
                  {heroRetailer} • {heroChannel}
                </p>
                <h1 className="text-4xl leading-tight font-semibold text-slate-900">
                  {heroClient} Performance Overview
                </h1>
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400"></span>
                  {heroDateRange}
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-sky-400"></span>
                  {data.records_processed} records processed
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-violet-400"></span>
                  {data.summary.total_days} days analyzed
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-4">
              <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4 shadow-inner">
                <ClientLogo
                  clientId={metadata?.client ?? null}
                  className="h-14 w-32 object-contain"
                  fallback={<div className="h-14 w-32 bg-slate-200 rounded-xl" />}
                />
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                Slide canvas · 13.33 in × 7.5 in
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 px-16 pb-14">
          <div className="grid grid-cols-12 gap-6 h-full">
            {template.modules.map((module) => {
              const span = module.span ?? 12;
              return (
                <div
                  key={module.id}
                  style={{
                    gridColumn: `span ${span} / span ${span}`,
                    minWidth: 0,
                  }}
                  className="text-slate-900"
                >
                  {renderTemplateModule(module, {
                    data,
                    template,
                    callouts,
                    metadata,
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});

WidescreenExportCanvas.displayName = "WidescreenExportCanvas";
